// src/components/TranslationProjects.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Container, Typography, Box, Button, Grid, Card, CardContent,
    LinearProgress, IconButton, Menu, MenuItem, Chip, Breadcrumbs,
    Link, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TableSortLabel, TextField, ToggleButtonGroup, ToggleButton,
    Tooltip, Autocomplete, Tabs, Tab, Drawer, Checkbox, FormControl, Select,
    InputLabel, Divider, Skeleton, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, Alert, Badge, Popover, Stack,
} from '@mui/material';
import Flag from 'react-world-flags';
import TranslateIcon from '@mui/icons-material/Translate';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import GrainIcon from '@mui/icons-material/Grain';

import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { logout } from '../slices/authSlice';
import isoLanguages from './isoLanguages';
import { getProjectStatus, statusColor, getProjectProgress } from '../utils/projectStatus';
import { stringToPastelColor, stringToDarkColor } from '../utils/colorUtils';

const INITIAL_VISIBLE_COUNT = 9;
const LOAD_MORE_STEP = 9;
const MAX_VISIBLE_TAGS = 3;

const getLanguageDetails = (code) =>
    isoLanguages.find((lang) => lang.code.toLowerCase() === code?.toLowerCase()) || {
        name: code,
        countryCode: 'UN',
    };

const getProjectLangCodes = (project) =>
    Array.isArray(project.targetLanguages)
        ? project.targetLanguages.map((l) => (typeof l === 'string' ? l : l.code))
        : [project.targetLanguage];

const getProjectLangs = (project) =>
    Array.isArray(project.targetLanguages)
        ? project.targetLanguages
        : [{ code: project.targetLanguage }];

const getProjectLangNames = (project) =>
    getProjectLangCodes(project).map((c) => getLanguageDetails(c).name);

const getFirstProjectId = (project) =>
    Array.isArray(project.targetLanguages) && project.targetLanguages.length > 0
        ? project.targetLanguages[0].projectId || project.targetLanguages[0]
        : project._id;

// Days until deadline (negative = overdue). null when no deadline.
const daysUntilDeadline = (deadline) => {
    if (!deadline) return null;
    const ms = new Date(deadline).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

const deadlineUrgency = (deadline) => {
    const d = daysUntilDeadline(deadline);
    if (d === null) return { color: 'text.secondary', label: '' };
    if (d < 0) return { color: 'error.main', label: `Overdue by ${Math.abs(d)}d`, severe: true };
    if (d <= 3) return { color: 'error.main', label: `Due in ${d}d`, severe: true };
    if (d <= 7) return { color: 'warning.main', label: `Due in ${d}d` };
    return { color: 'text.secondary', label: `Due ${new Date(deadline).toLocaleDateString()}` };
};

const getCounts = (project) => {
    const sections = Array.isArray(project.sections) ? project.sections : [];
    const segmentCount = typeof project.segmentCount === 'number' ? project.segmentCount : sections.length;
    const computedWordCount = typeof project.wordCount === 'number' ? project.wordCount : sections.reduce((sum, sec) => {
        const words = (sec || '').split(/\s+/).filter(Boolean).length;
        return sum + words;
    }, 0);
    return { segmentCount, computedWordCount };
};

// Smart filter predicates
const SMART_FILTERS = {
    all: { label: 'All', match: () => true },
    overdue: {
        label: 'Overdue',
        match: (p) => {
            const d = daysUntilDeadline(p.deadline);
            return d !== null && d < 0 && getProjectProgress(p) < 100;
        },
    },
    dueSoon: {
        label: 'Due this week',
        match: (p) => {
            const d = daysUntilDeadline(p.deadline);
            return d !== null && d >= 0 && d <= 7 && getProjectProgress(p) < 100;
        },
    },
    active: {
        label: 'Recently active',
        match: (p) => {
            if (!p.updatedAt) return false;
            const ms = Date.now() - new Date(p.updatedAt).getTime();
            return ms < 7 * 24 * 60 * 60 * 1000;
        },
    },
    ready: {
        label: 'Ready to export',
        match: (p) => getProjectProgress(p) >= 100,
    },
};

const SkeletonCard = () => (
    <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
            <Skeleton variant="text" width="60%" height={32} />
            <Box display="flex" gap={2} mt={1}>
                <Skeleton variant="rectangular" width={80} height={20} />
                <Skeleton variant="rectangular" width={80} height={20} />
            </Box>
            <Skeleton variant="text" width="40%" sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={8} sx={{ mt: 2, borderRadius: 1 }} />
            <Box display="flex" justifyContent="space-between" mt={2}>
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={80} />
            </Box>
        </CardContent>
    </Card>
);

const LanguagePair = ({ project, dense = false }) => {
    const src = getLanguageDetails(project.sourceLanguage);
    const targets = getProjectLangCodes(project).map(getLanguageDetails);
    const visible = targets.slice(0, 2);
    const extra = targets.length - visible.length;

    return (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Tooltip title={src.name}>
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Flag code={src.countryCode} style={{ width: dense ? 18 : 22 }} />
                    {!dense && <Typography variant="body2" color="text.secondary">{src.name}</Typography>}
                </Box>
            </Tooltip>
            <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                {visible.map((t, i) => (
                    <Tooltip key={i} title={t.name}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Flag code={t.countryCode} style={{ width: dense ? 18 : 22 }} />
                            {!dense && targets.length === 1 && (
                                <Typography variant="body2" color="text.secondary">{t.name}</Typography>
                            )}
                        </Box>
                    </Tooltip>
                ))}
                {extra > 0 && (
                    <Tooltip title={targets.slice(2).map(t => t.name).join(', ')}>
                        <Chip size="small" label={`+${extra}`} sx={{ height: 18, fontSize: '0.65rem' }} />
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

const TagsRow = ({ tags, dense = false }) => {
    if (!tags || tags.length === 0) return null;
    const visible = tags.slice(0, MAX_VISIBLE_TAGS);
    const extra = tags.length - visible.length;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {visible.map((tag, idx) => (
                <Chip
                    key={idx} label={tag} size="small"
                    sx={{
                        height: dense ? 16 : 20, fontSize: dense ? '0.6rem' : '0.65rem',
                        bgcolor: stringToPastelColor(tag), color: stringToDarkColor(tag),
                        fontWeight: 600, border: `1px solid ${stringToDarkColor(tag)}`,
                    }}
                />
            ))}
            {extra > 0 && (
                <Tooltip title={tags.slice(MAX_VISIBLE_TAGS).join(', ')}>
                    <Chip size="small" label={`+${extra}`} sx={{ height: dense ? 16 : 20, fontSize: dense ? '0.6rem' : '0.65rem' }} />
                </Tooltip>
            )}
        </Box>
    );
};

const ProjectCard = React.memo(function ProjectCard({
    project, isSharedView, isArchivedView, isPinned, isSelected, isDownloading,
    onOpen, onPin, onSelect, onInfo, onEdit, onTranslate, onDownload,
    onExportXliff, onDelete, onRename, onArchive,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [hover, setHover] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState(project.projectName || '');
    useEffect(() => { setNameDraft(project.projectName || ''); }, [project.projectName]);
    const commitRename = () => {
        const next = nameDraft.trim();
        setEditingName(false);
        if (next && next !== project.projectName) onRename(project, next);
        else setNameDraft(project.projectName || '');
    };
    const open = Boolean(anchorEl);
    const handleOpen = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
    const handleClose = () => setAnchorEl(null);

    const progress = getProjectProgress(project);
    const isFullyTranslated = progress >= 100;
    const status = getProjectStatus(project);
    const statusCol = statusColor[status] || 'default';
    const { segmentCount, computedWordCount } = getCounts(project);
    const urgency = deadlineUrgency(project.deadline);

    const targets = getProjectLangs(project);
    const showPerLangProgress = targets.length > 1;

    return (
        <Card
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={() => onOpen(project)}
            sx={{
                borderRadius: 3,
                boxShadow: isSelected ? 8 : 2,
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s',
                overflow: 'hidden',
                border: isSelected ? '2px solid' : '2px solid transparent',
                borderColor: isSelected ? 'primary.main' : 'transparent',
                bgcolor: isPinned ? 'action.hover' : 'background.paper',
                '&:hover': { boxShadow: 8, transform: 'translateY(-2px)' },
                mb: 3,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0, top: 0, bottom: 0, width: 4,
                    bgcolor: (theme) => theme.palette[statusCol]?.main || theme.palette.grey[400],
                    borderTopLeftRadius: 12, borderBottomLeftRadius: 12,
                },
            }}
        >
            <CardContent sx={{ position: 'relative', pl: 2.5 }}>
                <Box sx={{
                    position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex',
                    opacity: hover ? 1 : 0, transition: 'opacity 0.15s',
                }}>
                    <Tooltip title="Quick info">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onInfo(project); }}>
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={handleOpen}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Menu anchorEl={anchorEl} open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
                    {!isSharedView && (
                        <MenuItem onClick={() => { onEdit(project); handleClose(); }}>
                            <EditIcon fontSize="small" sx={{ mr: 1 }} />Edit
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => { onTranslate(project); handleClose(); }}>
                        <TranslateIcon fontSize="small" sx={{ mr: 1 }} />Translate
                    </MenuItem>
                    <MenuItem onClick={() => { onDownload(project, 'original'); handleClose(); }}>
                        <DownloadIcon fontSize="small" sx={{ mr: 1 }} />Download Original
                    </MenuItem>
                    <MenuItem disabled={!isFullyTranslated} onClick={() => { onDownload(project, 'translated'); handleClose(); }}>
                        <DownloadIcon fontSize="small" sx={{ mr: 1 }} />Download Translated
                    </MenuItem>
                    <MenuItem onClick={() => { onExportXliff(project); handleClose(); }}>
                        <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />Export as XLIFF
                    </MenuItem>
                    {!isSharedView && [
                        <Divider key="d" />,
                        <MenuItem key="arch" onClick={() => { onArchive(project, !isArchivedView); handleClose(); }}>
                            {isArchivedView
                                ? <><UnarchiveIcon fontSize="small" sx={{ mr: 1 }} />Unarchive</>
                                : <><ArchiveIcon fontSize="small" sx={{ mr: 1 }} />Archive</>}
                        </MenuItem>,
                        <MenuItem key="del" onClick={() => { onDelete(project); handleClose(); }} sx={{ color: 'error.main' }}>
                            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />Delete
                        </MenuItem>,
                    ]}
                </Menu>

                <Box display="flex" alignItems="center" gap={0.5} mb={1} mt={1} pr={6}>
                    <Checkbox
                        size="small"
                        checked={isSelected}
                        onClick={(e) => onSelect(e, project._id)}
                        sx={{
                            p: 0.5,
                            opacity: isSelected || hover ? 1 : 0,
                            transition: 'opacity 0.15s',
                            pointerEvents: isSelected || hover ? 'auto' : 'none',
                        }}
                    />
                    <IconButton size="small" onClick={(e) => onPin(e, project._id)} sx={{ p: 0.5 }}>
                        {isPinned
                            ? <PushPinIcon color="primary" fontSize="small" sx={{ transform: 'rotate(45deg)' }} />
                            : <PushPinOutlinedIcon fontSize="small" color="disabled" />}
                    </IconButton>
                    {editingName ? (
                        <TextField
                            value={nameDraft}
                            size="small"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commitRename();
                                if (e.key === 'Escape') { setEditingName(false); setNameDraft(project.projectName || ''); }
                            }}
                            onBlur={commitRename}
                            sx={{ flexGrow: 1 }}
                        />
                    ) : (
                        <Tooltip title={isSharedView ? '' : 'Double-click to rename'}>
                            <Typography
                                variant="h6" fontWeight={700}
                                onDoubleClick={(e) => { e.stopPropagation(); if (!isSharedView) setEditingName(true); }}
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, cursor: isSharedView ? 'inherit' : 'text' }}
                            >
                                {project.projectName}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>

                {isSharedView && (
                    <Box mb={1}>
                        <Chip size="small" label="Assigned" color="secondary" variant="outlined" />
                    </Box>
                )}

                <Box mb={1.5}><LanguagePair project={project} /></Box>

                <Box mb={1.5} display="flex" alignItems="center" gap={2.5}>
                    <Tooltip title={`${computedWordCount} words`}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <LibraryBooksIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">{computedWordCount.toLocaleString()}</Typography>
                        </Box>
                    </Tooltip>
                    <Tooltip title={`${segmentCount} segments`}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <GrainIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">{segmentCount.toLocaleString()}</Typography>
                        </Box>
                    </Tooltip>
                </Box>

                {project.tags && project.tags.length > 0 && (
                    <Box mb={1.5}><TagsRow tags={project.tags} /></Box>
                )}

                <Box mt={2}>
                    {showPerLangProgress ? (
                        <Stack spacing={0.5}>
                            {targets.map((t) => {
                                const tProgress = typeof t.progress === 'number' ? Math.floor(t.progress) : progress;
                                const lang = getLanguageDetails(t.code);
                                return (
                                    <Tooltip key={t.code} title={`${lang.name} — ${tProgress}%`} placement="left">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Flag code={lang.countryCode} style={{ width: 16 }} />
                                            <LinearProgress
                                                variant="determinate" value={tProgress}
                                                color={tProgress >= 100 ? 'success' : 'primary'}
                                                sx={{ flexGrow: 1, borderRadius: 2, height: 6 }}
                                            />
                                            <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>{tProgress}%</Typography>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    ) : (
                        <Tooltip title={`${progress}% Complete`} placement="top">
                            <Box>
                                <LinearProgress
                                    variant="determinate" value={progress}
                                    color={isFullyTranslated ? 'success' : 'primary'}
                                    sx={{ borderRadius: 2, height: 8 }}
                                />
                                <Box mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption">{progress}%</Typography>
                                    <Chip size="small" label={status} color={statusCol} sx={{ fontWeight: 700, textTransform: 'capitalize' }} />
                                </Box>
                            </Box>
                        </Tooltip>
                    )}
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {project.updatedAt ? `Updated ${new Date(project.updatedAt).toLocaleDateString()}` : ''}
                    </Typography>
                    {urgency.label && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {urgency.severe && <AccessTimeIcon sx={{ fontSize: 14, color: urgency.color }} />}
                            <Typography variant="caption" sx={{ color: urgency.color, fontWeight: urgency.severe ? 700 : 400 }}>
                                {urgency.label}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {isDownloading && (
                    <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }} />
                )}
            </CardContent>
        </Card>
    );
});

export default function TranslationProjects() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [createdProjects, setCreatedProjects] = useState([]);
    const [sharedProjects, setSharedProjects] = useState([]);
    const [archivedProjects, setArchivedProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // URL-synced state
    const tabValue = parseInt(searchParams.get('tab') || '0', 10);
    const search = searchParams.get('q') || '';
    const sortBy = searchParams.get('sort') || 'createdAt';
    const viewMode = searchParams.get('view') || 'card';
    const smartFilter = searchParams.get('smart') || 'all';
    const statusFilter = useMemo(() => {
        const s = searchParams.get('status');
        return s ? s.split(',').filter(Boolean) : [];
    }, [searchParams]);
    const tagFilter = useMemo(() => {
        const t = searchParams.get('tags');
        return t ? t.split(',').filter(Boolean) : [];
    }, [searchParams]);

    const updateParam = useCallback((key, value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
                next.delete(key);
            } else {
                next.set(key, Array.isArray(value) ? value.join(',') : String(value));
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const [allUniqueTags, setAllUniqueTags] = useState([]);
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

    const [pinnedProjects, setPinnedProjects] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('pinnedProjects')) || [];
        } catch {
            return [];
        }
    });

    const [selectedProjects, setSelectedProjects] = useState([]);
    const [drawerProject, setDrawerProject] = useState(null);

    const [downloadingProject, setDownloadingProject] = useState(null);
    const [downloadingType, setDownloadingType] = useState(null);

    const [deleteDialog, setDeleteDialog] = useState({ open: false, target: null, bulk: false });
    const [filterAnchor, setFilterAnchor] = useState(null);
    const [tableSort, setTableSort] = useState({ field: null, dir: 'asc' });

    const searchRef = useRef(null);

    const token = useSelector((state) => state.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [activeRes, archivedRes] = await Promise.all([
                axios.get('/api/projects?summary=true', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/projects?summary=true&archived=true', { headers: { Authorization: `Bearer ${token}` } }),
            ]);

            const created = activeRes.data.createdProjects || [];
            const shared = activeRes.data.sharedProjects || [];
            const archived = archivedRes.data.createdProjects || [];

            setCreatedProjects(created);
            setSharedProjects(shared);
            setArchivedProjects(archived);

            const tags = new Set();
            [...created, ...shared, ...archived].forEach((p) => (p.tags || []).forEach((t) => tags.add(t)));
            setAllUniqueTags(Array.from(tags).sort());
        } catch (err) {
            if (err.response?.status === 401) {
                dispatch(logout());
                navigate('/login', { replace: true });
            } else {
                console.error('Error fetching projects:', err);
                setError('Could not load projects. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [token, dispatch, navigate]);

    useEffect(() => {
        if (token) fetchProjects();
    }, [token, fetchProjects]);

    // Refetch when window regains focus
    useEffect(() => {
        const onFocus = () => { if (token) fetchProjects(); };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [token, fetchProjects]);

    useEffect(() => {
        setVisibleCount(INITIAL_VISIBLE_COUNT);
        setSelectedProjects([]);
    }, [search, statusFilter, tagFilter, tabValue, sortBy, smartFilter]);

    useEffect(() => {
        localStorage.setItem('pinnedProjects', JSON.stringify(pinnedProjects));
    }, [pinnedProjects]);

    // Keyboard shortcut: '/' focuses search
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const togglePin = useCallback((e, projectId) => {
        e.stopPropagation();
        setPinnedProjects((prev) => (
            prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
        ));
    }, []);

    const toggleSelection = useCallback((e, projectId) => {
        e.stopPropagation();
        setSelectedProjects((prev) => (
            prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
        ));
    }, []);

    const handleSelectAll = (e, projectsInView) => {
        e.stopPropagation();
        if (selectedProjects.length === projectsInView.length) {
            setSelectedProjects([]);
        } else {
            setSelectedProjects(projectsInView.map((p) => p._id));
        }
    };

    const handleDownload = useCallback(async (projectOrId, fileType = 'translated') => {
        const project = typeof projectOrId === 'string' ? null : projectOrId;
        const projectId = project ? getFirstProjectId(project) : projectOrId;
        setDownloadingProject(project ? project._id : projectId);
        setDownloadingType(fileType);
        try {
            const response = await axios.get(`/api/export/${projectId}/download?type=${fileType}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${projectId}-${fileType}`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) fileName = match[1];
            }
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
        } finally {
            setDownloadingProject(null);
            setDownloadingType(null);
        }
    }, [token]);

    const handleBulkDownload = async (type) => {
        for (const projId of selectedProjects) {
            await handleDownload(projId, type);
        }
        setSelectedProjects([]);
    };

    const handleExportAsXliff = useCallback(async (project) => {
        const projectId = getFirstProjectId(project);
        try {
            const response = await axios.get(`/api/projects/${projectId}/xliff`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/xml' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${projectId}.xliff`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting XLIFF:', err);
        }
    }, [token]);

    const performDelete = async (ids) => {
        for (const id of ids) {
            try {
                await axios.delete(`/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            } catch (err) {
                console.error('Error deleting project:', err);
            }
        }
        setCreatedProjects((prev) => prev.filter((p) => !ids.includes(p._id)));
        setSharedProjects((prev) => prev.filter((p) => !ids.includes(p._id)));
        setSelectedProjects((prev) => prev.filter((id) => !ids.includes(id)));
    };

    const confirmDelete = async () => {
        const { target, bulk } = deleteDialog;
        const ids = bulk ? [...selectedProjects] : [target._id];
        setDeleteDialog({ open: false, target: null, bulk: false });
        await performDelete(ids);
    };

    // Unified filtering logic
    const currentProjects = tabValue === 1 ? sharedProjects : (tabValue === 2 ? archivedProjects : createdProjects);
    const isArchivedView = tabValue === 2;
    const isSharedView = tabValue === 1;

    const filteredAndSortedProjects = useMemo(() => {
        const smartMatch = SMART_FILTERS[smartFilter]?.match || (() => true);
        const lowerSearch = search.toLowerCase();

        let filtered = currentProjects.filter((project) => {
            if (!smartMatch(project)) return false;

            const projectName = (project.projectName || '').toLowerCase();
            const status = getProjectStatus(project);
            const sourceLang = getLanguageDetails(project.sourceLanguage).name.toLowerCase();
            const targetNames = getProjectLangNames(project).join(' ').toLowerCase();

            const matchesSearch = !lowerSearch
                || projectName.includes(lowerSearch)
                || status.includes(lowerSearch)
                || sourceLang.includes(lowerSearch)
                || targetNames.includes(lowerSearch);

            const matchesStatus = statusFilter.length === 0 || statusFilter.includes(status);
            const matchesTags = tagFilter.length === 0 || (project.tags && tagFilter.every((tag) => project.tags.includes(tag)));

            return matchesSearch && matchesStatus && matchesTags;
        });

        filtered.sort((a, b) => {
            const aPinned = pinnedProjects.includes(a._id);
            const bPinned = pinnedProjects.includes(b._id);
            if (aPinned && !bPinned) return -1;
            if (!aPinned && bPinned) return 1;

            // Table column sort wins when active
            if (viewMode === 'list' && tableSort.field) {
                const dir = tableSort.dir === 'asc' ? 1 : -1;
                switch (tableSort.field) {
                    case 'name':
                        return dir * (a.projectName || '').localeCompare(b.projectName || '');
                    case 'progress':
                        return dir * (getProjectProgress(a) - getProjectProgress(b));
                    case 'deadline': {
                        const da = a.deadline ? new Date(a.deadline).getTime() : Infinity;
                        const db = b.deadline ? new Date(b.deadline).getTime() : Infinity;
                        return dir * (da - db);
                    }
                    case 'status':
                        return dir * getProjectStatus(a).localeCompare(getProjectStatus(b));
                    default:
                        break;
                }
            }

            if (sortBy === 'deadline') {
                const dateA = a.deadline ? new Date(a.deadline) : new Date(8640000000000000);
                const dateB = b.deadline ? new Date(b.deadline) : new Date(8640000000000000);
                return dateA - dateB;
            } else if (sortBy === 'progress') {
                return getProjectProgress(b) - getProjectProgress(a);
            } else if (sortBy === 'name') {
                return (a.projectName || '').localeCompare(b.projectName || '');
            } else if (sortBy === 'updatedAt') {
                return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
            }
            return new Date(b.createdAt) - new Date(a.createdAt);
        });

        return filtered;
    }, [currentProjects, search, statusFilter, tagFilter, sortBy, pinnedProjects, smartFilter, viewMode, tableSort]);

    // Smart filter counts
    const smartCounts = useMemo(() => {
        const counts = {};
        Object.entries(SMART_FILTERS).forEach(([key, def]) => {
            counts[key] = currentProjects.filter(def.match).length;
        });
        return counts;
    }, [currentProjects]);

    const displayedProjects = filteredAndSortedProjects.slice(0, visibleCount);

    const toggleStatus = (statusKey) => {
        const next = statusFilter.includes(statusKey)
            ? statusFilter.filter((s) => s !== statusKey)
            : [...statusFilter, statusKey];
        updateParam('status', next);
    };

    const handleSortClick = (field) => {
        setTableSort((prev) => {
            if (prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return { field: null, dir: 'asc' };
        });
    };

    const activeFilterCount = statusFilter.length + tagFilter.length + (smartFilter !== 'all' ? 1 : 0);

    const breadcrumbs = location.pathname
        .split('/')
        .filter((x) => x)
        .map((path, idx, arr) => (
            <Link
                key={path}
                color={idx === arr.length - 1 ? 'text.primary' : 'inherit'}
                onClick={() => navigate(`/${arr.slice(0, idx + 1).join('/')}`)}
                sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
            >
                {path}
            </Link>
        ));

    const handleRename = useCallback(async (project, newName) => {
        const prevName = project.projectName;
        // Optimistic update across all buckets
        const apply = (list) => list.map((p) => (p._id === project._id ? { ...p, projectName: newName } : p));
        setCreatedProjects(apply);
        setSharedProjects(apply);
        setArchivedProjects(apply);
        try {
            await axios.put(
                `/api/projects/${project._id}`,
                { projectName: newName },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            console.error('Rename failed:', err);
            const revert = (list) => list.map((p) => (p._id === project._id ? { ...p, projectName: prevName } : p));
            setCreatedProjects(revert);
            setSharedProjects(revert);
            setArchivedProjects(revert);
            setError('Rename failed. Please try again.');
        }
    }, [token]);

    const handleArchive = useCallback(async (project, archive) => {
        try {
            await axios.put(
                `/api/projects/${project._id}/archive`,
                { archive },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Move between buckets locally rather than full refetch
            if (archive) {
                setCreatedProjects((prev) => prev.filter((p) => p._id !== project._id));
                setArchivedProjects((prev) => [{ ...project, archivedAt: new Date().toISOString() }, ...prev]);
            } else {
                setArchivedProjects((prev) => prev.filter((p) => p._id !== project._id));
                setCreatedProjects((prev) => [{ ...project, archivedAt: null }, ...prev]);
            }
            setSelectedProjects((prev) => prev.filter((id) => id !== project._id));
        } catch (err) {
            console.error('Archive failed:', err);
            setError('Archive operation failed.');
        }
    }, [token]);

    const handleBulkArchive = async (archive) => {
        const ids = [...selectedProjects];
        setSelectedProjects([]);
        for (const id of ids) {
            const project = currentProjects.find((p) => p._id === id);
            if (project) await handleArchive(project, archive);
        }
    };

    const handlers = {
        onOpen: (p) => navigate(`/dashboard/projects/${p._id}`),
        onPin: togglePin,
        onSelect: toggleSelection,
        onInfo: (p) => setDrawerProject(p),
        onEdit: (p) => navigate(`/dashboard/projects/${p._id}/edit`),
        onTranslate: (p) => navigate(`/dashboard/projects/${getFirstProjectId(p)}/translate`),
        onDownload: (p, type) => handleDownload(p, type),
        onExportXliff: handleExportAsXliff,
        onDelete: (p) => setDeleteDialog({ open: true, target: p, bulk: false }),
        onRename: handleRename,
        onArchive: handleArchive,
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 5 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link color="inherit" onClick={() => navigate('/dashboard/projects')} sx={{ cursor: 'pointer' }}>
                    <FolderIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Projects
                </Link>
                {breadcrumbs}
            </Breadcrumbs>

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <Typography variant="h4" fontWeight={800}>Translation Projects</Typography>
                <Button
                    variant="contained" size="large" startIcon={<AddIcon />}
                    onClick={() => navigate('/dashboard/new-translation')}
                >
                    Create New Project
                </Button>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={(e, v) => updateParam('tab', v === 0 ? '' : v)}>
                    <Tab label={`My Projects (${createdProjects.length})`} />
                    <Tab label={`Shared With Me (${sharedProjects.length})`} />
                    <Tab
                        icon={<Inventory2OutlinedIcon fontSize="small" />}
                        iconPosition="start"
                        label={`Archived (${archivedProjects.length})`}
                    />
                </Tabs>
            </Box>

            {/* Smart filter row */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {Object.entries(SMART_FILTERS).map(([key, def]) => (
                    <Chip
                        key={key}
                        label={`${def.label}${key !== 'all' ? ` (${smartCounts[key]})` : ''}`}
                        size="small"
                        clickable
                        color={smartFilter === key ? 'primary' : 'default'}
                        variant={smartFilter === key ? 'filled' : 'outlined'}
                        onClick={() => updateParam('smart', key === 'all' ? '' : key)}
                    />
                ))}
            </Box>

            {/* Toolbar: search + filters + sort + view */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small" variant="outlined"
                    placeholder="Search projects…  (press / to focus)"
                    value={search}
                    inputRef={searchRef}
                    onChange={(e) => updateParam('q', e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    sx={{ minWidth: 280, flexGrow: 1, maxWidth: 420 }}
                />

                <Badge badgeContent={activeFilterCount} color="primary">
                    <Button
                        variant="outlined" size="medium" startIcon={<FilterListIcon />}
                        onClick={(e) => setFilterAnchor(e.currentTarget)}
                    >
                        Filters
                    </Button>
                </Badge>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select value={sortBy} label="Sort By" onChange={(e) => updateParam('sort', e.target.value === 'createdAt' ? '' : e.target.value)}>
                        <MenuItem value="createdAt">Date Created</MenuItem>
                        <MenuItem value="updatedAt">Last Updated</MenuItem>
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="deadline">Deadline</MenuItem>
                        <MenuItem value="progress">Progress</MenuItem>
                    </Select>
                </FormControl>

                <Box sx={{ flexGrow: 1 }} />

                <ToggleButtonGroup
                    value={viewMode} exclusive
                    onChange={(_, v) => v && updateParam('view', v === 'card' ? '' : v)}
                    size="small"
                >
                    <ToggleButton value="card"><ViewModuleIcon /></ToggleButton>
                    <ToggleButton value="list"><ViewListIcon /></ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Popover
                open={Boolean(filterAnchor)} anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2.5, width: 320 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Status</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {['in progress', 'translated', 'complete'].map((statusKey) => (
                            <Chip
                                key={statusKey} label={statusKey} size="small" clickable
                                color={statusColor[statusKey]}
                                onClick={() => toggleStatus(statusKey)}
                                variant={statusFilter.includes(statusKey) ? 'filled' : 'outlined'}
                                sx={{ textTransform: 'capitalize' }}
                            />
                        ))}
                    </Box>

                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Tags</Typography>
                    <Autocomplete
                        multiple size="small" options={allUniqueTags} value={tagFilter}
                        onChange={(e, newValue) => updateParam('tags', newValue)}
                        renderInput={(params) => <TextField {...params} placeholder="Filter by tags" />}
                    />

                    {activeFilterCount > 0 && (
                        <Button
                            fullWidth size="small" sx={{ mt: 2 }}
                            onClick={() => {
                                updateParam('status', []);
                                updateParam('tags', []);
                                updateParam('smart', '');
                            }}
                        >
                            Clear all filters
                        </Button>
                    )}
                </Box>
            </Popover>

            {selectedProjects.length > 0 && (
                <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {selectedProjects.length} selected
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" color="inherit" size="small" sx={{ color: 'primary.main' }} onClick={() => handleBulkDownload('translated')}>
                        Download Translated
                    </Button>
                    <Button variant="contained" color="inherit" size="small" sx={{ color: 'primary.main' }} onClick={() => handleBulkDownload('original')}>
                        Download Original
                    </Button>
                    {tabValue === 0 && (
                        <Button
                            variant="outlined" color="inherit" size="small"
                            startIcon={<ArchiveIcon />}
                            onClick={() => handleBulkArchive(true)}
                        >
                            Archive
                        </Button>
                    )}
                    {isArchivedView && (
                        <Button
                            variant="outlined" color="inherit" size="small"
                            startIcon={<UnarchiveIcon />}
                            onClick={() => handleBulkArchive(false)}
                        >
                            Unarchive
                        </Button>
                    )}
                    {!isSharedView && (
                        <Button
                            variant="outlined" color="inherit" size="small"
                            onClick={() => setDeleteDialog({ open: true, target: null, bulk: true })}
                        >
                            Delete
                        </Button>
                    )}
                    <Button size="small" color="inherit" onClick={() => setSelectedProjects([])}>Clear</Button>
                </Paper>
            )}

            {error && (
                <Alert
                    severity="error" sx={{ mb: 3 }}
                    action={<Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={fetchProjects}>Retry</Button>}
                >
                    {error}
                </Alert>
            )}

            {loading ? (
                <Grid container spacing={3}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}><SkeletonCard /></Grid>
                    ))}
                </Grid>
            ) : filteredAndSortedProjects.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center', mt: 4, bgcolor: 'background.default' }} variant="outlined">
                    <FolderIcon sx={{ fontSize: 72, color: 'text.disabled', mb: 2 }} />
                    {currentProjects.length === 0 ? (
                        <>
                            <Typography variant="h6" gutterBottom>
                                {tabValue === 0
                                    ? 'No projects yet'
                                    : isSharedView
                                        ? 'Nothing shared with you yet'
                                        : 'No archived projects'}
                            </Typography>
                            <Typography color="text.secondary" mb={3}>
                                {tabValue === 0
                                    ? 'Create your first translation project to get started.'
                                    : isSharedView
                                        ? 'When teammates share a project with you, it will appear here.'
                                        : 'Archive a project from its menu to move it here.'}
                            </Typography>
                            {tabValue === 0 && (
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/dashboard/new-translation')}>
                                    Create First Project
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography variant="h6" gutterBottom>No projects match your filters</Typography>
                            <Typography color="text.secondary" mb={3}>Try adjusting search, status, or tag filters.</Typography>
                            <Button variant="outlined" onClick={() => {
                                updateParam('q', '');
                                updateParam('status', []);
                                updateParam('tags', []);
                                updateParam('smart', '');
                            }}>
                                Clear filters
                            </Button>
                        </>
                    )}
                </Paper>
            ) : viewMode === 'card' ? (
                <>
                    <Grid container spacing={3}>
                        {displayedProjects.map((project) => (
                            <Grid item xs={12} sm={6} md={4} key={project._id}>
                                <ProjectCard
                                    project={project}
                                    isSharedView={isSharedView}
                                    isArchivedView={isArchivedView}
                                    isPinned={pinnedProjects.includes(project._id)}
                                    isSelected={selectedProjects.includes(project._id)}
                                    isDownloading={downloadingProject === project._id}
                                    {...handlers}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    {filteredAndSortedProjects.length > visibleCount && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button variant="outlined" onClick={() => setVisibleCount((prev) => prev + LOAD_MORE_STEP)}>
                                Show More ({filteredAndSortedProjects.length - visibleCount} remaining)
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectedProjects.length > 0 && selectedProjects.length < displayedProjects.length}
                                            checked={displayedProjects.length > 0 && selectedProjects.length === displayedProjects.length}
                                            onChange={(e) => handleSelectAll(e, displayedProjects)}
                                        />
                                    </TableCell>
                                    <TableCell width={32}></TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={tableSort.field === 'name'}
                                            direction={tableSort.field === 'name' ? tableSort.dir : 'asc'}
                                            onClick={() => handleSortClick('name')}
                                        >Project Name</TableSortLabel>
                                    </TableCell>
                                    <TableCell>Languages</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={tableSort.field === 'progress'}
                                            direction={tableSort.field === 'progress' ? tableSort.dir : 'asc'}
                                            onClick={() => handleSortClick('progress')}
                                        >Progress</TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={tableSort.field === 'deadline'}
                                            direction={tableSort.field === 'deadline' ? tableSort.dir : 'asc'}
                                            onClick={() => handleSortClick('deadline')}
                                        >Deadline</TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={tableSort.field === 'status'}
                                            direction={tableSort.field === 'status' ? tableSort.dir : 'asc'}
                                            onClick={() => handleSortClick('status')}
                                        >Status</TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedProjects.map((project) => {
                                    const progress = getProjectProgress(project);
                                    const isPinned = pinnedProjects.includes(project._id);
                                    const isSelected = selectedProjects.includes(project._id);
                                    const urgency = deadlineUrgency(project.deadline);
                                    const status = getProjectStatus(project);
                                    return (
                                        <TableRow
                                            key={project._id} hover
                                            sx={{ cursor: 'pointer', '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                            selected={isSelected}
                                            onClick={() => navigate(`/dashboard/projects/${project._id}`)}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isSelected} onClick={(e) => toggleSelection(e, project._id)} />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={(e) => togglePin(e, project._id)}>
                                                    {isPinned ? <PushPinIcon color="primary" fontSize="small" sx={{ transform: 'rotate(45deg)' }} /> : <PushPinOutlinedIcon fontSize="small" color="disabled" />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography fontWeight={isPinned ? 700 : 500}>{project.projectName}</Typography>
                                                    <TagsRow tags={project.tags} dense />
                                                </Box>
                                            </TableCell>
                                            <TableCell><LanguagePair project={project} dense /></TableCell>
                                            <TableCell sx={{ minWidth: 140 }}>
                                                <Tooltip title={`${progress}%`}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate" value={progress}
                                                            color={progress >= 100 ? 'success' : 'primary'}
                                                            sx={{ flexGrow: 1, borderRadius: 2, height: 6 }}
                                                        />
                                                        <Typography variant="caption">{progress}%</Typography>
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: urgency.color, fontWeight: urgency.severe ? 700 : 400 }}>
                                                    {urgency.label || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={status} color={statusColor[status]} sx={{ textTransform: 'capitalize' }} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                                    <Tooltip title="Quick info">
                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDrawerProject(project); }}>
                                                            <InfoOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Translate">
                                                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/projects/${getFirstProjectId(project)}/translate`); }}>
                                                            <TranslateIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <RowMenu
                                                        project={project}
                                                        isSharedView={isSharedView}
                                                        isArchivedView={isArchivedView}
                                                        onEdit={handlers.onEdit}
                                                        onDownload={handlers.onDownload}
                                                        onExportXliff={handlers.onExportXliff}
                                                        onDelete={handlers.onDelete}
                                                        onArchive={handlers.onArchive}
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {filteredAndSortedProjects.length > visibleCount && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button variant="outlined" onClick={() => setVisibleCount((prev) => prev + LOAD_MORE_STEP)}>
                                Show More ({filteredAndSortedProjects.length - visibleCount} remaining)
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {/* Quick Peek Drawer */}
            <Drawer anchor="right" open={drawerProject !== null} onClose={() => setDrawerProject(null)}>
                <Box sx={{ width: 380, p: 3 }}>
                    {drawerProject && (() => {
                        const progress = getProjectProgress(drawerProject);
                        const status = getProjectStatus(drawerProject);
                        const { segmentCount, computedWordCount } = getCounts(drawerProject);
                        const urgency = deadlineUrgency(drawerProject.deadline);
                        return (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6" fontWeight={700}>Quick Peek</Typography>
                                    <IconButton onClick={() => setDrawerProject(null)} size="small"><CloseIcon /></IconButton>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Typography variant="h5" fontWeight={700} mb={1}>{drawerProject.projectName}</Typography>
                                <Box display="flex" gap={1} mb={2}>
                                    <Chip size="small" label={status} color={statusColor[status]} sx={{ textTransform: 'capitalize' }} />
                                    {urgency.label && (
                                        <Chip size="small" label={urgency.label} variant="outlined" sx={{ color: urgency.color, borderColor: urgency.color }} />
                                    )}
                                </Box>

                                <Typography variant="body2" color="text.secondary" mb={3}>
                                    {drawerProject.description || 'No description provided.'}
                                </Typography>

                                <Box mb={2}>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Overall progress</Typography>
                                    <LinearProgress
                                        variant="determinate" value={progress}
                                        color={progress >= 100 ? 'success' : 'primary'}
                                        sx={{ borderRadius: 2, height: 8 }}
                                    />
                                    <Typography variant="caption">{progress}%</Typography>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Box display="flex" alignItems="center" gap={2} mb={1}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Source</Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Flag code={getLanguageDetails(drawerProject.sourceLanguage).countryCode} style={{ width: 20 }} />
                                            <Typography variant="body2">{getLanguageDetails(drawerProject.sourceLanguage).name}</Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box mb={2}>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Targets</Typography>
                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                        {getProjectLangs(drawerProject).map((t, i) => {
                                            const lang = getLanguageDetails(t.code);
                                            const tp = typeof t.progress === 'number' ? Math.floor(t.progress) : progress;
                                            return (
                                                <Box key={i} display="flex" alignItems="center" gap={1}>
                                                    <Flag code={lang.countryCode} style={{ width: 18 }} />
                                                    <Typography variant="body2" sx={{ minWidth: 100 }}>{lang.name}</Typography>
                                                    <LinearProgress variant="determinate" value={tp} sx={{ flexGrow: 1, height: 4, borderRadius: 1 }} />
                                                    <Typography variant="caption" sx={{ minWidth: 32, textAlign: 'right' }}>{tp}%</Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="caption" color="text.secondary">Words</Typography>
                                    <Typography variant="body2" fontWeight={600}>{computedWordCount.toLocaleString()}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="caption" color="text.secondary">Segments</Typography>
                                    <Typography variant="body2" fontWeight={600}>{segmentCount.toLocaleString()}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="caption" color="text.secondary">Created</Typography>
                                    <Typography variant="body2">{new Date(drawerProject.createdAt).toLocaleDateString()}</Typography>
                                </Box>
                                {drawerProject.updatedAt && (
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography variant="caption" color="text.secondary">Last update</Typography>
                                        <Typography variant="body2">{new Date(drawerProject.updatedAt).toLocaleDateString()}</Typography>
                                    </Box>
                                )}

                                {drawerProject.tags && drawerProject.tags.length > 0 && (
                                    <Box mt={2}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Tags</Typography>
                                        <TagsRow tags={drawerProject.tags} />
                                    </Box>
                                )}

                                <Stack spacing={1} mt={3}>
                                    <Button
                                        fullWidth variant="contained" startIcon={<TranslateIcon />}
                                        onClick={() => navigate(`/dashboard/projects/${getFirstProjectId(drawerProject)}/translate`)}
                                    >
                                        Open Translator
                                    </Button>
                                    <Button
                                        fullWidth variant="outlined"
                                        onClick={() => navigate(`/dashboard/projects/${drawerProject._id}`)}
                                    >
                                        View Full Dashboard
                                    </Button>
                                </Stack>
                            </>
                        );
                    })()}
                </Box>
            </Drawer>

            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, target: null, bulk: false })}>
                <DialogTitle>Delete {deleteDialog.bulk ? `${selectedProjects.length} projects` : 'project'}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteDialog.bulk
                            ? `This will permanently delete ${selectedProjects.length} projects and their translations. This cannot be undone.`
                            : `"${deleteDialog.target?.projectName}" and all its translations will be permanently deleted. This cannot be undone.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, target: null, bulk: false })}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

function RowMenu({ project, isSharedView, isArchivedView, onEdit, onDownload, onExportXliff, onDelete, onArchive }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const progress = getProjectProgress(project);
    const isFullyTranslated = progress >= 100;

    return (
        <>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}>
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} onClick={(e) => e.stopPropagation()}>
                {!isSharedView && (
                    <MenuItem onClick={() => { onEdit(project); setAnchorEl(null); }}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />Edit
                    </MenuItem>
                )}
                <MenuItem onClick={() => { onDownload(project, 'original'); setAnchorEl(null); }}>
                    <DownloadIcon fontSize="small" sx={{ mr: 1 }} />Download Original
                </MenuItem>
                <MenuItem disabled={!isFullyTranslated} onClick={() => { onDownload(project, 'translated'); setAnchorEl(null); }}>
                    <DownloadIcon fontSize="small" sx={{ mr: 1 }} />Download Translated
                </MenuItem>
                <MenuItem onClick={() => { onExportXliff(project); setAnchorEl(null); }}>
                    <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />Export as XLIFF
                </MenuItem>
                {!isSharedView && [
                    <Divider key="d" />,
                    <MenuItem key="arch" onClick={() => { onArchive(project, !isArchivedView); setAnchorEl(null); }}>
                        {isArchivedView
                            ? <><UnarchiveIcon fontSize="small" sx={{ mr: 1 }} />Unarchive</>
                            : <><ArchiveIcon fontSize="small" sx={{ mr: 1 }} />Archive</>}
                    </MenuItem>,
                    <MenuItem key="del" onClick={() => { onDelete(project); setAnchorEl(null); }} sx={{ color: 'error.main' }}>
                        <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />Delete
                    </MenuItem>,
                ]}
            </Menu>
        </>
    );
}
